import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
    Copy,
    Check,
    Download,
    Upload,
    ArrowLeftRight,
    FileImage,
    Trash2,
} from "lucide-react";

// ── Component ────────────────────────────────────────────────

export function Base64Tool() {
    const [text, setText] = useState("");
    const [base64, setBase64] = useState("");
    const [direction, setDirection] = useState<"encode" | "decode">("encode");
    const [error, setError] = useState<string | null>(null);
    const [copied, setCopied] = useState<"text" | "b64" | null>(null);
    const [imagePreview, setImagePreview] = useState<string | null>(null);

    useEffect(() => {
        document.title =
            "Base64 Encode & Decode — Free Online Converter — Ballah Code";
        const meta = document.querySelector('meta[name="description"]');
        if (meta) {
            meta.setAttribute(
                "content",
                "Free Base64 encoder and decoder. Convert text to Base64 and back, upload files, preview images. Runs entirely in your browser.",
            );
        }
    }, []);

    // Auto-convert
    useEffect(() => {
        setError(null);
        setImagePreview(null);

        if (direction === "encode") {
            if (!text.trim()) {
                setBase64("");
                return;
            }
            try {
                setBase64(btoa(unescape(encodeURIComponent(text))));
            } catch {
                setError("Failed to encode");
            }
        } else {
            if (!base64.trim()) {
                setText("");
                return;
            }
            try {
                const decoded = decodeURIComponent(escape(atob(base64.replace(/\s/g, ""))));
                setText(decoded);

                // Check if it's an image
                const trimmed = base64.trim();
                if (
                    trimmed.startsWith("/9j/") || // JPEG
                    trimmed.startsWith("iVBOR") || // PNG
                    trimmed.startsWith("R0lGOD") || // GIF
                    trimmed.startsWith("UklGR") // WebP
                ) {
                    const mime = trimmed.startsWith("/9j/")
                        ? "image/jpeg"
                        : trimmed.startsWith("iVBOR")
                            ? "image/png"
                            : trimmed.startsWith("R0lGOD")
                                ? "image/gif"
                                : "image/webp";
                    setImagePreview(`data:${mime};base64,${trimmed}`);
                }
            } catch {
                setError("Invalid Base64 string");
                setText("");
            }
        }
    }, [text, base64, direction]);

    const copyText = async (value: string, which: "text" | "b64") => {
        await navigator.clipboard.writeText(value);
        setCopied(which);
        setTimeout(() => setCopied(null), 2000);
    };

    const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;
        const reader = new FileReader();
        reader.onload = (ev) => {
            const result = ev.target?.result as string;
            // Remove data URL prefix to get raw base64
            const b64 = result.split(",")[1] || result;
            setDirection("decode");
            setBase64(b64);

            // Preview for images
            if (file.type.startsWith("image/")) {
                setImagePreview(result);
            }
        };
        reader.readAsDataURL(file);
        e.target.value = "";
    };

    const downloadDecoded = () => {
        const blob = new Blob([text], { type: "text/plain" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = "decoded.txt";
        a.click();
        URL.revokeObjectURL(url);
    };

    return (
        <div className="min-h-screen bg-background text-foreground font-sans">
            {/* Nav */}
            <nav className="flex items-center justify-between px-8 py-[18px]">
                <Link
                    to="/"
                    className="text-sm font-semibold tracking-[-0.03em] text-zinc-200 hover:text-white transition-colors"
                >
                    ballah code
                </Link>
                <div className="flex items-center gap-6 text-[13px] text-zinc-600">
                    <Link to="/tools" className="hover:text-zinc-400 transition-colors">
                        tools
                    </Link>
                    <Link to="/" className="hover:text-zinc-400 transition-colors">
                        home
                    </Link>
                </div>
            </nav>

            <main className="max-w-[1280px] mx-auto px-8 pt-12 pb-24">
                {/* Breadcrumb */}
                <div className="flex items-center gap-2 text-[13px] text-zinc-600 mb-8">
                    <Link to="/tools" className="hover:text-zinc-400 transition-colors">
                        Tools
                    </Link>
                    <span className="text-zinc-700">/</span>
                    <span className="text-zinc-400">Base64 Encode / Decode</span>
                </div>

                {/* Header */}
                <div className="mb-8">
                    <h1 className="text-3xl font-medium tracking-[-0.03em] mb-3">
                        Base64 Encode & Decode
                    </h1>
                    <p className="text-zinc-500 text-[15px] leading-relaxed max-w-2xl">
                        Convert text to Base64 and back. Upload files to get their Base64
                        representation. Image files are automatically previewed.
                    </p>
                </div>

                {/* Controls */}
                <div className="flex items-center gap-3 mb-4">
                    <button
                        onClick={() =>
                            setDirection((d) => (d === "encode" ? "decode" : "encode"))
                        }
                        className="flex items-center gap-2 px-4 py-2.5 rounded-lg border border-white/[0.06] bg-[#111113] text-[13px] text-zinc-400 hover:text-zinc-200 hover:border-white/[0.1] transition-colors"
                    >
                        <ArrowLeftRight size={14} />
                        {direction === "encode" ? "Text → Base64" : "Base64 → Text"}
                    </button>

                    <label className="flex items-center gap-1.5 px-3 py-2.5 rounded-lg border border-white/[0.06] text-[12px] text-zinc-500 hover:text-zinc-300 hover:border-white/[0.1] cursor-pointer transition-colors">
                        <Upload size={12} />
                        Upload file
                        <input
                            type="file"
                            onChange={handleFileUpload}
                            className="hidden"
                        />
                    </label>

                    {(text || base64) && (
                        <button
                            onClick={() => {
                                setText("");
                                setBase64("");
                                setError(null);
                                setImagePreview(null);
                            }}
                            className="flex items-center gap-1.5 px-3 py-2.5 rounded-lg border border-white/[0.06] text-[12px] text-zinc-600 hover:text-zinc-400 transition-colors"
                        >
                            <Trash2 size={12} />
                            Clear
                        </button>
                    )}
                </div>

                {/* Panels */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
                    {/* Text panel */}
                    <div className="flex flex-col">
                        <div className="flex items-center justify-between px-4 py-2.5 bg-[#0d0d0f] border border-white/[0.06] border-b-0 rounded-t-xl">
                            <span className="text-[11px] text-zinc-600 font-mono uppercase tracking-wider">
                                {direction === "encode" ? "Text (input)" : "Text (output)"}
                            </span>
                            <div className="flex items-center gap-1">
                                {text && (
                                    <>
                                        <button
                                            onClick={() => copyText(text, "text")}
                                            className="flex items-center gap-1 px-2 py-1 rounded text-[11px] text-zinc-600 hover:text-zinc-300 transition-colors"
                                        >
                                            {copied === "text" ? (
                                                <Check size={11} className="text-green-400" />
                                            ) : (
                                                <Copy size={11} />
                                            )}
                                        </button>
                                        {direction === "decode" && (
                                            <button
                                                onClick={downloadDecoded}
                                                className="flex items-center gap-1 px-2 py-1 rounded text-[11px] text-zinc-600 hover:text-zinc-300 transition-colors"
                                            >
                                                <Download size={11} />
                                            </button>
                                        )}
                                    </>
                                )}
                            </div>
                        </div>
                        <textarea
                            value={text}
                            onChange={(e) => {
                                if (direction === "encode") setText(e.target.value);
                            }}
                            readOnly={direction === "decode"}
                            placeholder={
                                direction === "encode"
                                    ? "Type or paste text to encode..."
                                    : "Decoded text will appear here..."
                            }
                            spellCheck={false}
                            className={`w-full min-h-[300px] bg-[#111113] border border-white/[0.06] rounded-b-xl p-4 text-[13px] text-zinc-300 font-mono leading-relaxed outline-none resize-y placeholder:text-zinc-800 transition-colors ${direction === "encode"
                                ? "focus:border-white/[0.1]"
                                : "opacity-80"
                                }`}
                        />
                    </div>

                    {/* Base64 panel */}
                    <div className="flex flex-col">
                        <div className="flex items-center justify-between px-4 py-2.5 bg-[#0d0d0f] border border-white/[0.06] border-b-0 rounded-t-xl">
                            <span className="text-[11px] text-zinc-600 font-mono uppercase tracking-wider">
                                {direction === "decode" ? "Base64 (input)" : "Base64 (output)"}
                            </span>
                            <div className="flex items-center gap-1">
                                {base64 && (
                                    <button
                                        onClick={() => copyText(base64, "b64")}
                                        className="flex items-center gap-1 px-2 py-1 rounded text-[11px] text-zinc-600 hover:text-zinc-300 transition-colors"
                                    >
                                        {copied === "b64" ? (
                                            <Check size={11} className="text-green-400" />
                                        ) : (
                                            <Copy size={11} />
                                        )}
                                    </button>
                                )}
                            </div>
                        </div>
                        <textarea
                            value={base64}
                            onChange={(e) => {
                                if (direction === "decode") setBase64(e.target.value);
                            }}
                            readOnly={direction === "encode"}
                            placeholder={
                                direction === "decode"
                                    ? "Paste Base64 string to decode..."
                                    : "Base64 output will appear here..."
                            }
                            spellCheck={false}
                            className={`w-full min-h-[300px] bg-[#111113] border border-white/[0.06] rounded-b-xl p-4 text-[13px] text-zinc-300 font-mono leading-relaxed outline-none resize-y placeholder:text-zinc-800 transition-colors ${direction === "decode"
                                ? "focus:border-white/[0.1]"
                                : "opacity-80"
                                }`}
                        />
                    </div>
                </div>

                {/* Error */}
                {error && (
                    <div className="text-[13px] text-red-400 mb-4">{error}</div>
                )}

                {/* Image preview */}
                {imagePreview && (
                    <div className="mb-8">
                        <div className="flex items-center gap-2 text-[12px] text-zinc-600 mb-2">
                            <FileImage size={12} />
                            Image preview
                        </div>
                        <div className="inline-block border border-white/[0.06] rounded-xl overflow-hidden bg-[#0d0d0f] p-2">
                            <img
                                src={imagePreview}
                                alt="Decoded"
                                className="max-w-[400px] max-h-[300px] rounded-lg"
                            />
                        </div>
                    </div>
                )}

                {/* Stats */}
                {(text || base64) && (
                    <div className="flex items-center gap-6 text-[12px] text-zinc-600 mb-16">
                        {text && (
                            <span>Text: {new Blob([text]).size} bytes</span>
                        )}
                        {base64 && (
                            <span>Base64: {base64.length} chars</span>
                        )}
                        {text && base64 && (
                            <span>
                                Ratio: {((base64.length / new Blob([text]).size) * 100).toFixed(0)}%
                            </span>
                        )}
                    </div>
                )}

                {/* SEO content */}
                <section className="max-w-2xl border-t border-white/[0.04] pt-12">
                    <h2 className="text-lg font-medium tracking-[-0.02em] mb-4 text-zinc-300">
                        What is Base64 encoding?
                    </h2>
                    <div className="space-y-4 text-[14.5px] text-zinc-500 leading-[1.75]">
                        <p>
                            Base64 is a binary-to-text encoding scheme that converts binary
                            data into ASCII characters. It's commonly used to embed images in
                            CSS and HTML, encode API tokens, and transmit binary data over
                            text-based protocols like email and JSON.
                        </p>
                        <p>
                            Base64 increases the data size by approximately 33%, making files
                            larger but safely transportable as text. This tool handles
                            encoding and decoding of any text, and automatically detects and
                            previews image data.
                        </p>
                        <p>
                            Everything runs entirely in your browser — no data is ever sent
                            to a server. Built by the team behind{" "}
                            <Link
                                to="/"
                                className="text-violet-400 hover:text-violet-300 transition-colors"
                            >
                                Ballah Code
                            </Link>
                            .
                        </p>
                    </div>
                    <div className="mt-8 flex flex-wrap gap-2">
                        <span className="text-[12px] text-zinc-700 mr-1 self-center">Related →</span>
                        <Link to="/tools/jwt-decoder" className="text-[12px] text-zinc-500 hover:text-violet-400 transition-colors px-2.5 py-1 rounded-md border border-white/[0.06] hover:border-violet-400/30">JWT Decoder</Link>
                        <Link to="/tools/jwt-encoder" className="text-[12px] text-zinc-500 hover:text-violet-400 transition-colors px-2.5 py-1 rounded-md border border-white/[0.06] hover:border-violet-400/30">JWT Encoder</Link>
                        <Link to="/tools/url-encode-decode" className="text-[12px] text-zinc-500 hover:text-violet-400 transition-colors px-2.5 py-1 rounded-md border border-white/[0.06] hover:border-violet-400/30">URL Encode / Decode</Link>
                        <Link to="/tools/hash-generator" className="text-[12px] text-zinc-500 hover:text-violet-400 transition-colors px-2.5 py-1 rounded-md border border-white/[0.06] hover:border-violet-400/30">Hash Generator</Link>
                    </div>
                </section>
            </main>

            {/* Footer */}
            <footer className="border-t border-white/[0.04] py-5 px-8 flex justify-between max-w-[1280px] mx-auto text-xs text-zinc-800">
                <Link to="/" className="hover:text-zinc-600 transition-colors">
                    ballah code
                </Link>
                <Link to="/tools" className="hover:text-zinc-600 transition-colors">
                    tools
                </Link>
                <span>ballah tech</span>
            </footer>
        </div>
    );
}
